import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';

import { SvbrgCategoryType } from '@/generated/gql/graphql';
import { IntakeDomainType } from '~/common/types';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { IntakePageTitle } from '~/components/IntakePageTitle';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { RouteId } from '~/routes';

import { useIntakeListDataGridConfig } from './IntakeList.dataGrid';
import { useIntakeListDataGridQueryBuilderConfig } from './IntakeList.queryBuilder';
import { IntakeListDataGridColumn } from './IntakeListDataGrid.types';
import { INTAKE_THREAD_LIST_QUERY } from './IntakeListThreads.query.gql';

const domain = IntakeDomainType.INTAKE;

export const IntakeList = () => {
  const apiRef = useGridApiRef();
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={<IntakePageTitle domain={domain} />}
            maxWidth={false}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  overflow: 'hidden',
                  alignItems: 'center',
                }}
              >
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
              </Stack>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useIntakeListDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      <DataGridWidget
        apiRef={apiRef}
        domain={domain}
        loading={loading}
        pinnedColumns={{
          right: [IntakeListDataGridColumn.ACTIONS],
        }}
        filterCount={rulesTotal}
        useConfig={useIntakeListDataGridConfig}
        initialNavigateDetail={({ row }) => ({ to: RouteId.intakeDetail(row.id) })}
        loadingProps={{
          query: INTAKE_THREAD_LIST_QUERY,
          getNodes: (data) => data.sevenbridges.threadList.nodes,
          getTotalCount: (data) => data.sevenbridges.threadList.totalCount || 0,
        }}
        additionalVariables={{
          categories: [SvbrgCategoryType.INTAKE],
        }}
      />
    </PageLayout>
  );
};
